import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";

import { Reaction } from "../types";
import { WorkplaceReviewCommentAuthorInfo } from "./CommentAuthorInfo";
import { WorkplaceReviewCommentLikesButton } from "./LikesButton";
import { WorkplaceReviewCommentRepliesAndLikesWrapper } from "./RepliesAndLikesWrapper";
import { WorkplaceReviewCommentRepliesCountIcon } from "./RepliesCountIcon";
import { ReviewContextPills } from "./ReviewContextPills";
import { WorkplaceReviewAnonymizeIcon } from "./WorkplaceReviewAnonymizeIcon";
import { WorkplaceReviewRemoveIcon } from "./WorkplaceReviewRemoveIcon";

interface CardDelayedLayoutProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  profilePictureUrl?: string;
  isLoadingProfilePicture: boolean;
  createdAt: Date;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymize: () => void;
  onRemove: () => void;
}

export function CardDelayedLayout(props: CardDelayedLayoutProps) {
  const {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl,
    isLoadingProfilePicture,
    createdAt,
    onLikeChanged,
    onAnonymize,
    onRemove,
  } = props;

  const {
    workerInfo,
    text,
    aggregates,
    workerReaction,
    shiftPeriod,
    shiftWorkerQualification,
    patientsPerNurse,
    reviewTag,
    rating,
  } = comment.attributes;
  const { name: workerName } = workerInfo;
  const { totalLikes, totalReplies } = aggregates;

  return (
    <>
      <Stack spacing={4}>
        <WorkplaceReviewCommentAuthorInfo
          useDelayedReviewLayout
          workerName={workerName}
          profilePictureUrl={profilePictureUrl}
          isLoadingProfilePicture={isLoadingProfilePicture}
          createdAt={createdAt}
          shiftWorkerQualification={shiftWorkerQualification}
        />
        <ReviewContextPills
          shiftPeriod={shiftPeriod}
          patientsPerNurse={patientsPerNurse}
          reviewTag={reviewTag}
          rating={rating}
        />
        <Text variant="body2">{text}</Text>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ marginTop: 4 }}>
        <WorkplaceReviewCommentRepliesAndLikesWrapper>
          {viewerIsAuthor && <WorkplaceReviewAnonymizeIcon onAnonymize={onAnonymize} />}

          <WorkplaceReviewCommentRepliesCountIcon count={totalReplies} />

          <WorkplaceReviewCommentLikesButton
            workplaceId={workplaceId}
            commentId={comment.id}
            isLiked={workerReaction === Reaction.LIKE}
            count={totalLikes}
            onLikeChanged={onLikeChanged}
          />

          {viewerIsAuthor && <WorkplaceReviewRemoveIcon onRemove={onRemove} />}
        </WorkplaceReviewCommentRepliesAndLikesWrapper>
      </Stack>
    </>
  );
}
